/* eslint-disable complexity */
import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import Scroll from 'react-scroll';
import { customInspectionTypeFrontEndSettings } from '../../customInspectionTypes/customInspectionTypeFrontEndSettings';
import { ITranslateProps, translate } from '../../mixins/translate';
import { fetchGet } from '../../store/fetch';
import { AssetTypes, AssetTypes as assetTypes } from '../../utils/enums/AssetTypes';
import { InspectionStandardTypes } from '../../utils/enums/InspectionStandardTypes';
import { Roles } from '../../utils/enums/Roles';
import { formatBytes } from '../../utils/formatBytes';
import { IRouterProps } from '../../utils/IRouterProps';
import { isCustomerVersion } from '../../utils/isCustomerVersion';
import { isEemuaOrApi } from '../../utils/isEemuaOrApi';
import {
    LeftTray,
    LeftTrayItem,
    LeftTrayList,
    PanelLink,
    ScrollMenu,
    ScrollMenuItem,
    Topbar,
    TopbarCrumb,
    TopbarCrumbActive,
    TopbarLeft,
    TopbarRight,
    Tray,
} from '../elements';
import { SurveyDetailsEquipmentUsage } from '../Equipment/Common/SurveyDetailsEquipmentUsage';
import { IMaintenanceMeasure } from '../interfaces/IMaintenanceMeasure';
import { DetailsDocuments } from '../Objects/DetailsDocuments';
import { Spinner } from '../spinners';
import { Component } from '../utils/Component';
import { ConditionScoreLabel } from '../utils/ConditionScoreLabel';
import { DisplayMessage } from '../utils/displayMessage';
import { DownloadReport } from '../utils/DownloadReport';
import { NullableFormattedDate } from '../utils/NullableFormattedDate';
import RoleDependent from '../utils/RoleDependent';
import { SurveyStatusLabel } from '../utils/SurveyStatusLabel';
import { TagLabels } from '../utils/TagLabels';
import { Annotation2D } from './Common/Annotation2D';
import { InspectionPlans } from './Common/InspectionPlans';
import { loadSurveyElements } from './Common/loadSurveyElements';
import { MailShareLink } from './Common/MailShareLink';
import { MaintenanceListNenFemca } from './Common/MaintenanceListNENFMECA';
import { Potree3D } from './Common/Potree3D';
import { ReportImage } from './Common/ReportImage';
import SummeryAndAdvice from './Common/SummeryAndAdvice';
import { SurveyDetailsSignature } from './Common/SurveyDetailsSignature';
import { VideoList } from './Common/Videos';
import { VideoStream } from './Common/VideoStream';
import { ConditionReportCra } from './CRA/ConditionReportCra';
import { ConditionReportEEMUA } from './EEMUA/ConditionReportEEMUA';
import RepairList from './EEMUA/RepairList';
import { ConditionReportFMECA } from './FMECA/ConditionReportFMECA';
import RiskAssessment from './FMECA/RiskAssessment';
import { SurveyDetailsFmecaFurtherInvestigations } from './FMECA/SurveyDetailsFmecaFurtherInvestigations';
import { IAsset } from './Interfaces/IAsset';
import { ICategory } from './Interfaces/ICategory';
import { IElement } from './Interfaces/IElement';
import { IFailureMode } from './Interfaces/IFailureMode';
import { IInspectionPlan } from './Interfaces/IInspectionPlan';
import { ISurvey } from './Interfaces/ISurvey';
import { ConditionReportNEN } from './NEN/ConditionReportNEN';
import { SurveyCopy } from './SurveyCopy';

const { scroller } = Scroll;

interface ISurveyDetailsProps extends ITranslateProps, IRouterProps {
    dispatch: (prop: any) => void;
    userRole: Roles;
    isTempLogin: boolean;
}

type LeftTrayVisibilitySettings = {
    equipmentUsage: boolean;
};

interface ISurveyDetailsState extends ISurvey {
    breakDownOpenState: { [key: string]: boolean };
    pageElements: {
        showFmecaFurtherInvestigations?: boolean;
    };
    elements: IElement[];
    categories: ICategory[];
    failureModes: IFailureMode[];
    maintenanceMeasures: IMaintenanceMeasure[];
    inspectionPlans: IInspectionPlan[];
    isLoadingElements: boolean;
    isLoadingSurveyDetails: boolean;
    ifcAsset?: IAsset;
    plyAsset?: IAsset;
    leftTrayVisibilitySettings: LeftTrayVisibilitySettings;
    statusCode?: number;
}

function showDocuments(inspectionStandardType: string | undefined) {
    if (!inspectionStandardType) {
        return false;
    }
    if (
        [
            InspectionStandardTypes.Api570,
            InspectionStandardTypes.Api653,
            InspectionStandardTypes.Eemua159,
            InspectionStandardTypes.Free,
            InspectionStandardTypes.Fmeca,
            InspectionStandardTypes.Nen2767,
            InspectionStandardTypes.Memo,
            InspectionStandardTypes.Model3d,
        ].includes(inspectionStandardType as unknown as InspectionStandardTypes)
    ) {
        return true;
    }
    const settings = customInspectionTypeFrontEndSettings[inspectionStandardType];
    if (settings) {
        return settings.showDocuments;
    }
    return false;
}

function responseStatusCodeToMessage(statusCode: number | undefined, translateKey: (key: string) => string) {
    switch (statusCode) {
        case 400:
            return translateKey('common.invalidId');
        case 404:
            return translateKey('common.doesNotExistAnymore');
        case 403:
            return translateKey('common.noAccess');
        default:
            return `invalid request status code ${statusCode}`;
    }
}

function mapStateToProps(state: any) {
    const { authentication } = state;
    return {
        userRole: authentication.role,
        isTempLogin: authentication.isTempLogin,
    };
}

class SurveyDetailsComponent extends Component<ISurveyDetailsProps, ISurveyDetailsState> {
    constructor(props: ISurveyDetailsProps) {
        super(props);

        this.state = {
            isLoadingSurveyDetails: true,
            isLoadingElements: false,
            breakDownOpenState: {},
            elements: [],
            categories: [],
            failureModes: [],
            inspectionPlans: [],
            startPageAssets: [],
            sameTypeSurveysForObject: [],
            maintenanceMeasures: [],
            noData: false,
            isCraAvailable: false,
            isFmecaAvailable: false,
            inspectionStandardData: {},
            labels: [],
            pageElements: {},
            hasPointClouds: false,
            leftTrayVisibilitySettings: {
                equipmentUsage: false,
            },
        };
    }

    public override async componentDidMount() {
        await this.load();
    }

    public override render() {
        const isFree = this.state.inspectionStandardType === InspectionStandardTypes.Free;
        const isFmeca = this.state.inspectionStandardType === InspectionStandardTypes.Fmeca;
        const isNen = this.state.inspectionStandardType === InspectionStandardTypes.Nen2767;
        const isMemo = this.state.inspectionStandardType === InspectionStandardTypes.Memo;

        // eslint-disable-next-line @typescript-eslint/naming-convention
        const LeftTrayListItems = this.getLeftTrayListItems();
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const ScrollMenuItems = this.getScrollMenuItems();
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const DetailComponents = this.getDetailComponents();

        return (
            <>
                <Topbar>
                    <TopbarLeft>
                        <TopbarCrumbActive title={this.state.name} icon="fa-camera" />
                        <TopbarCrumb>
                            <Link to={`/objects/${this.state.objectId}`}>{this.props.t('common.objectDetails')}</Link>
                        </TopbarCrumb>
                        <TopbarCrumb>
                            <Link to="/objects">{this.props.t('common.objects')}</Link>
                        </TopbarCrumb>
                    </TopbarLeft>
                    {!this.state.noData && (
                        <TopbarRight>
                            <SurveyCopy surveyId={this.props.match.params.id} />
                            {(isFree || isEemuaOrApi(this.state.inspectionStandardType) || isNen) && (
                                <RoleDependent
                                    allowed={[Roles.Administrator, Roles.Operator, Roles.Surveyor, Roles.Client]}
                                    isPublic={this.state.objectIsPublic}
                                    content={
                                        <PanelLink
                                            href={`/api/surveys/${this.state.id}/export`}
                                            text="Data export"
                                            icon="fa fa-download"
                                            small={true}
                                            isDownload={true}
                                        />
                                    }
                                />
                            )}
                            {(isFree || isNen || isFmeca) && (
                                <DownloadReport
                                    url={`/api/surveys/${this.state.id}/makePdf`}
                                    text={this.props.t('survey.downloadPDFReport')}
                                    reportTitle={`${this.props.t('report.inspectionReport')}_${this.state.name?.trim()}`}
                                />
                            )}
                            {isNen && (
                                <PanelLink
                                    href={`/api/surveys/${this.state.id}/nenExcel`}
                                    text={this.props.t('survey.downloadExcelReport')}
                                    icon="fa fa-download"
                                    small={true}
                                    isDownload={true}
                                />
                            )}

                            <RoleDependent
                                allowed={[Roles.Administrator, Roles.Client]}
                                content={<>{this.state && this.state.id && this.state.canEditSurvey && <MailShareLink surveyId={this.state.id} />}</>}
                            />

                            <RoleDependent
                                allowed={[Roles.Administrator, Roles.Operator, Roles.Surveyor, Roles.Client]}
                                isPublic={this.state.objectIsPublic}
                                content={
                                    <>
                                        {this.state.canEditSurvey && (
                                            <a href="#" className="btn btn-default btn-sm fw600 ml10" onClick={(e) => this.editReport(e)}>
                                                <span className="fa fa fa-edit"></span> {this.props.t('common.edit')}
                                            </a>
                                        )}
                                    </>
                                }
                            />
                        </TopbarRight>
                    )}
                </Topbar>

                <section id="content" className="table-layout animated fadeIn">
                    <LeftTray>
                        <LeftTrayList>
                            <LeftTrayItem>{this.state.description}</LeftTrayItem>
                            {(this.state.referenceNumber && (
                                <LeftTrayItem title={this.props.t('survey.referenceNumber')}>{this.state.referenceNumber}</LeftTrayItem>
                            )) || <LeftTrayItem title={this.props.t('common.projectNumber')}>{this.state.projectNumber}</LeftTrayItem>}

                            {this.state.revisionNumber && (
                                <LeftTrayItem title={this.props.t('survey.revisionNumber')}>{this.state.revisionNumber}</LeftTrayItem>
                            )}
                            <LeftTrayItem title={this.props.t('common.object')}>
                                {' '}
                                <Link to={`/objects/${this.state.objectId}`}>{this.state.name}</Link>
                            </LeftTrayItem>
                            {this.state.inspectionStandardType && (
                                <LeftTrayItem title={this.props.t('common.inspectionTemplate')}>
                                    {this.props.t(`inspectionStandard.${this.state.inspectionStandardType}`)}
                                </LeftTrayItem>
                            )}
                            {this.state.objectUsage && <LeftTrayItem title={this.props.t('object.usage')}>{this.state.objectUsage}</LeftTrayItem>}
                            {this.state.objectConstructionYear && (
                                <LeftTrayItem title={this.props.t('object.constructionYear')}>{this.state.objectConstructionYear}</LeftTrayItem>
                            )}
                            <LeftTrayItem title={this.props.t('common.location')}>{this.state.objectLocation}</LeftTrayItem>
                            <LeftTrayItem title={this.props.t('common.operator')}>{this.state.operatorName}</LeftTrayItem>
                            <LeftTrayItem title={this.props.t('common.surveyor')}>{this.state.surveyorName}</LeftTrayItem>
                            {!this.props.isTempLogin && (
                                <LeftTrayItem title={this.props.t('common.owner')}>
                                    {' '}
                                    <Link to={`/objects?ownerId=${this.state.ownerCompanyId}`}>{this.state.ownerName}</Link>
                                </LeftTrayItem>
                            )}
                            <LeftTrayItem title={this.props.t('common.date')}>
                                <NullableFormattedDate value={this.state.surveryedOn} />
                            </LeftTrayItem>
                            <LeftTrayItem title={this.props.t('survey.size')}>{formatBytes(this.state.storageUsed ?? 0)} </LeftTrayItem>
                            <LeftTrayItem title={this.props.t('common.status')}>
                                <SurveyStatusLabel value={this.state.status} />{' '}
                            </LeftTrayItem>
                            {isNen && (
                                <LeftTrayItem title={this.props.t('common.condition')}>
                                    <ConditionScoreLabel value={this.state.condition} />
                                </LeftTrayItem>
                            )}
                            {isNen && this.state.careCondition && (
                                <LeftTrayItem title={this.props.t('common.care')}>
                                    <ConditionScoreLabel value={this.state.careCondition} />
                                </LeftTrayItem>
                            )}
                            <LeftTrayListItems survey={this.state} />

                            <LeftTrayItem>
                                <TagLabels labels={this.state.labels} urlPathname="/surveys" />
                            </LeftTrayItem>
                        </LeftTrayList>
                        <ScrollMenu>
                            <ScrollMenuItems scrollTo={this.scrollTo} />

                            {[
                                InspectionStandardTypes.Api653,
                                InspectionStandardTypes.Api570,
                                InspectionStandardTypes.Eemua159,
                                InspectionStandardTypes.Eemua159,
                                InspectionStandardTypes.Fmeca,
                                InspectionStandardTypes.Free,
                                InspectionStandardTypes.Memo,
                                InspectionStandardTypes.Model3d,
                                InspectionStandardTypes.Nen2767,
                            ].includes(this.state.inspectionStandardType!) &&
                                this.state.summaryAndAdvice && (
                                    <ScrollMenuItem
                                        text={this.props.t('survey.summaryAndAdvice')}
                                        icon="fa fa-file-text-o"
                                        onClick={() => this.scrollTo('SummaryAndAdvice')}
                                    />
                                )}
                            {isFmeca && this.state.isCraAvailable && (
                                <ScrollMenuItem text={this.props.t('cra.cra')} icon="fa fa-cog" onClick={() => this.scrollTo('RiskAssessment')} />
                            )}
                            {/* 2D model */}
                            {this.state.startPageAssets
                                .filter((asset: IAsset) => asset.type === assetTypes.ImageAnnotation)
                                .map((asset: IAsset, i: number) => (
                                    <ScrollMenuItem
                                        key={`${i}annotation`}
                                        text={asset.description ?? ''}
                                        icon="fa fa-image"
                                        onClick={() => this.scrollTo(asset.id)}
                                    />
                                ))}
                            {(this.state.hasPointClouds || this.state.plyAsset || this.state.ifcAsset) && (
                                <ScrollMenuItem text={this.props.t('survey.3dModel')} icon="fa fa-cube" onClick={() => this.scrollTo('3DModel')} />
                            )}
                            {this.state.startPageAssets
                                .filter((asset: IAsset) => asset.type === assetTypes.Image)
                                .map((asset: IAsset, i: number) => (
                                    <ScrollMenuItem
                                        key={`${i}image`}
                                        text={asset.description ?? ''}
                                        icon="fa fa-image"
                                        onClick={() => this.scrollTo(asset.id)}
                                    />
                                ))}
                            {(isEemuaOrApi(this.state.inspectionStandardType) || isFree || isMemo || isNen) && (
                                <ScrollMenuItem
                                    text={this.props.t('survey.conditionAssessment')}
                                    icon="fa fa-tasks"
                                    onClick={() => this.scrollTo('ConditionAssessment')}
                                />
                            )}
                            {isFmeca && this.state.isCraAvailable && (
                                <ScrollMenuItem
                                    text={this.props.t('survey.conditionReport')}
                                    icon="fa fa-tasks"
                                    onClick={() => this.scrollTo('CRAConditionAssessment')}
                                />
                            )}
                            {isFmeca && this.state.isFmecaAvailable && (
                                <ScrollMenuItem
                                    text={`FMECA ${this.props.t('survey.conditionReport')}`}
                                    icon="fa fa-tasks"
                                    onClick={() => this.scrollTo('FMECAConditionAssessment')}
                                />
                            )}
                            {(isNen || isFmeca) && (
                                <ScrollMenuItem
                                    text={this.props.t('maintenanceMeasure.maintenanceMeasures')}
                                    icon="fa fa-wrench"
                                    onClick={() => this.scrollTo('Maintenance')}
                                />
                            )}
                            {this.state.inspectionPlans && this.state.inspectionPlans.length > 0 && (
                                <ScrollMenuItem
                                    text={this.props.t('survey.inspectionPlans')}
                                    icon="fa fa-calendar-o"
                                    onClick={() => this.scrollTo('InspectionPlans')}
                                />
                            )}
                            {this.state.leftTrayVisibilitySettings.equipmentUsage && (
                                <ScrollMenuItem
                                    text={this.props.t('equipment.usedEquipment')}
                                    icon="fa fa-plane"
                                    onClick={() => this.scrollTo('Equipment')}
                                />
                            )}
                            {showDocuments(this.state.inspectionStandardType) && (
                                <ScrollMenuItem
                                    text={this.props.t('survey.documents')}
                                    icon="fa fa-book"
                                    onClick={() => this.scrollTo('Documents')}
                                />
                            )}
                            {this.state.isVideoDownloadVisible && (
                                <ScrollMenuItem text={this.props.t('video.videos')} icon="fa fa-film" onClick={() => this.scrollTo('Videos')} />
                            )}
                            {this.state.inspectionPlans && this.state.inspectionPlans.length > 0 && (
                                <ScrollMenuItem
                                    text={this.props.t('survey.inspectionPlans')}
                                    icon="fa fa-calendar-o"
                                    onClick={() => this.scrollTo('InspectionPlans')}
                                />
                            )}
                            {(this.state.preparedAuthor || this.state.verifiedAuthor) && (
                                <ScrollMenuItem
                                    text={this.props.t('survey.signature')}
                                    icon="fa fa-gavel"
                                    onClick={() => this.scrollTo('Signature')}
                                />
                            )}

                            {this.state.pageElements.showFmecaFurtherInvestigations && (
                                <ScrollMenuItem
                                    text={this.props.t('fmecaFurtherInvestigation.furtherInvestigation')}
                                    icon="fa fa-table"
                                    onClick={() => this.scrollTo('FmecaFurtherInvestigations')}
                                />
                            )}
                        </ScrollMenu>
                    </LeftTray>

                    <Tray>
                        {this.state.isLoadingSurveyDetails && <Spinner />}

                        {this.state.noData && (
                            <DisplayMessage
                                message={`${this.props.t('survey.notAvailable')}: ${responseStatusCodeToMessage(this.state.statusCode, this.props.t)}`}
                                messageType="error"
                            />
                        )}

                        {this.state.id && <DetailComponents survey={this.state as Required<ISurvey>} />}

                        {/* Summery and advice */}
                        {[
                            InspectionStandardTypes.Api653,
                            InspectionStandardTypes.Api570,
                            InspectionStandardTypes.Eemua159,
                            InspectionStandardTypes.Eemua159,
                            InspectionStandardTypes.Fmeca,
                            InspectionStandardTypes.Free,
                            InspectionStandardTypes.Memo,
                            InspectionStandardTypes.Model3d,
                            InspectionStandardTypes.Nen2767,
                        ].includes(this.state.inspectionStandardType!) &&
                            (this.state.summaryAndAdvice || isMemo) && (
                                <SummeryAndAdvice condition={this.state.condition} summaryAndAdvice={this.state.summaryAndAdvice} />
                            )}

                        {/* Vide Stream */}
                        {isCustomerVersion('ronik') && <VideoStream surveyId={this.props.match.params.id} />}

                        {/* Riskassessment*/}
                        {isFmeca && this.state.isCraAvailable && <RiskAssessment surveyDetails={this.state} />}

                        {/* 2D model */}
                        {this.state.startPageAssets
                            .filter((asset: IAsset) => asset.type === assetTypes.ImageAnnotation)
                            .map((asset: IAsset, i: number) => (
                                <Annotation2D
                                    key={i}
                                    reportImage={asset.id}
                                    reportImageTitle={asset.description ?? ''}
                                    objectId={this.state.objectId!}
                                    surveyId={this.state.id}
                                />
                            ))}

                        {/* 3D model */}
                        {(this.state.hasPointClouds || this.state.plyAsset || this.state.ifcAsset) && (
                            <>
                                <Potree3D
                                    objectId={this.state.objectId!}
                                    surveyId={this.state.id!}
                                    isAnnotationsDefaultVisible={this.state.isAnnotationsDefaultVisible!}
                                />
                            </>
                        )}

                        {/* Report image */}
                        {this.state.startPageAssets
                            .filter((asset: IAsset) => asset.type === assetTypes.Image)
                            .map((asset: IAsset, i: number) => (
                                <ReportImage
                                    key={i}
                                    reportImage={asset.id}
                                    reportImageTitle={asset.description ?? ''}
                                    reportImageExternalUrl={asset.externalUrl}
                                />
                            ))}

                        {/* condition report */}
                        {(isFree || isNen) && (
                            <ConditionReportNEN
                                surveyId={this.state.id}
                                elements={this.state.elements}
                                maintenanceMeasures={this.state.maintenanceMeasures}
                                breakDownOpenState={this.state.breakDownOpenState}
                                isAssetDescriptionVisible={!!this.state.isAssetDescriptionVisible}
                                onExpandAll={this.onExpandAll.bind(this)}
                                onCollapseAll={this.onCollapseAll.bind(this)}
                                onExpandDecomposition={this.onExpandDecomposition.bind(this)}
                                onChangeBreakdownOpenState={this.onChangeBreakdownOpenState.bind(this)}
                                isCareScoreAvailable={isNen && this.state.careCondition !== ''}
                                isCompareAvailable={this.state.sameTypeSurveysForObject && this.state.sameTypeSurveysForObject.length > 0}
                                isNen={isNen}
                                isLoadingElements={this.state.isLoadingElements}
                                constructionYear={this.state.objectConstructionYear}
                            />
                        )}

                        {isFmeca && this.state.isCraAvailable && (
                            <ConditionReportCra
                                objectId={this.state.objectId!}
                                surveyId={this.state.id!}
                                isAssetDescriptionVisible={!!this.state.isAssetDescriptionVisible}
                                elements={this.state.elements}
                            />
                        )}

                        {isFmeca && this.state.isFmecaAvailable && (
                            <ConditionReportFMECA
                                surveyId={this.state.id!}
                                elements={this.state.elements}
                                failureModes={this.state.failureModes}
                                breakDownOpenState={this.state.breakDownOpenState}
                                isAssetDescriptionVisible={!!this.state.isAssetDescriptionVisible}
                                onExpandAll={this.onExpandAll.bind(this)}
                                onCollapseAll={this.onCollapseAll.bind(this)}
                                onChangeBreakdownOpenState={this.onChangeBreakdownOpenState.bind(this)}
                                isLoadingElements={this.state.isLoadingElements}
                            />
                        )}

                        {isEemuaOrApi(this.state.inspectionStandardType) && (
                            <ConditionReportEEMUA
                                surveyId={this.state.id}
                                categories={this.state.categories}
                                elements={this.state.elements}
                                breakDownOpenState={this.state.breakDownOpenState}
                                isAssetDescriptionVisible={!!this.state.isAssetDescriptionVisible}
                                inspectionStandardType={this.state.inspectionStandardType!}
                                onExpandAll={this.onExpandAll.bind(this)}
                                onCollapseAll={this.onCollapseAll.bind(this)}
                                onChangeBreakdownOpenState={this.onChangeBreakdownOpenState.bind(this)}
                                isLoadingElements={this.state.isLoadingElements}
                                constructionYear={this.state.objectConstructionYear}
                            />
                        )}

                        {/* Repair adive  */}
                        {isEemuaOrApi(this.state.inspectionStandardType) && this.state.elements.length > 0 && (
                            <RepairList elements={this.state.elements} categories={this.state.categories} />
                        )}

                        {(isNen || isFmeca) && (
                            <MaintenanceListNenFemca
                                maintenanceMeasures={this.state.maintenanceMeasures}
                                surveyId={this.state.id}
                                inspectionStandardType={this.state.inspectionStandardType}
                                onChanged={async () => this.loadMaintenanceMeasurements()}
                            />
                        )}

                        {this.state.id && (
                            <SurveyDetailsEquipmentUsage
                                setLeftTrayVisible={this.setHasLeftTrayItem.bind(this, 'equipmentUsage')}
                                surveyId={this.state.id}
                            />
                        )}

                        {this.state.id && this.state.objectId && showDocuments(this.state.inspectionStandardType) && (
                            <DetailsDocuments
                                surveyId={this.state.id}
                                objectId={this.state.objectId}
                                isIncludeImages={
                                    this.state.inspectionStandardType === InspectionStandardTypes.Memo ||
                                    this.state.inspectionStandardType === InspectionStandardTypes.Model3d
                                }
                                context="survey"
                            />
                        )}

                        {/* Videos */}
                        {this.state.isVideoDownloadVisible && <VideoList surveyId={this.props.match.params.id} />}

                        {/* Inspectionplan */}
                        {this.state.inspectionPlans && this.state.inspectionPlans.length > 0 && (
                            <InspectionPlans inspectionPlans={this.state.inspectionPlans} surveyId={this.props.match.params.id} />
                        )}
                        {this.state.pageElements.showFmecaFurtherInvestigations && (
                            <SurveyDetailsFmecaFurtherInvestigations surveyId={this.props.match.params.id} />
                        )}
                        {/* Signature */}
                        {(this.state.preparedAuthor || this.state.verifiedAuthor) && <SurveyDetailsSignature surveyDetails={this.state} />}
                    </Tray>
                </section>
            </>
        );
    }

    private onChangeBreakdownOpenState(elementId: string) {
        const { breakDownOpenState } = this.state;
        breakDownOpenState[elementId] = !breakDownOpenState[elementId];
        void this.setState({ breakDownOpenState });
    }

    private async setHasLeftTrayItem(itemName: keyof LeftTrayVisibilitySettings, isVisible: boolean) {
        await this.setStatePromised((current) => ({ leftTrayVisibilitySettings: { ...current.leftTrayVisibilitySettings, [itemName]: isVisible } }));
    }

    private async load() {
        const url = `/api/surveys/${this.props.match.params.id}`;
        const result = await fetchGet(url, {}, [], [400, 403, 404]);
        if ([404, 403, 400].includes(result.status)) {
            await this.setState({ noData: true, isLoadingSurveyDetails: false, statusCode: result.status });
        } else {
            await this.setState(result.json);
            await this.loadStartPageAssets();
            await this.setState({ isLoadingSurveyDetails: false, isLoadingElements: true });

            const elements = await loadSurveyElements(this.props.match.params.id, this.state.objectId, this.state.inspectionStandardType);
            await this.setState({ elements, isLoadingElements: false });

            if (
                this.state.inspectionStandardType === InspectionStandardTypes.Fmeca ||
                this.state.inspectionStandardType === InspectionStandardTypes.Nen2767
            ) {
                await this.loadMaintenanceMeasurements();
            }

            if (this.state.inspectionStandardType === InspectionStandardTypes.Fmeca) {
                await this.loadFailureModes();
                await this.loadInspectionPlans();
                await this.setState({ pageElements: { ...this.state.pageElements, ...{ showFmecaFurtherInvestigations: true } } });
            }

            if (isEemuaOrApi(this.state.inspectionStandardType)) {
                await this.loadCategories(this.state.objectId!);
            }

            const plyAsset = await this.loadAsset(AssetTypes.PlyModel);
            const ifcAsset = await this.loadAsset(AssetTypes.IfcModel);
            await this.setState({ plyAsset, ifcAsset });
        }
    }

    private async loadAsset(assetType: AssetTypes) {
        const result = await fetchGet('/api/assets', {
            surveyId: this.state.id,
            types: `${assetType}`,
        });
        return result.json[0];
    }

    private async loadCategories(objectId: string) {
        const url = `/api/objects/${objectId}/categories`;
        const result = await fetchGet(url);
        await this.setState({ categories: result.json });
    }

    private async loadMaintenanceMeasurements() {
        const url = `/api/surveys/${this.props.match.params.id}/maintenanceMeasures`;
        const result = await fetchGet(url, {});
        await this.setState({ maintenanceMeasures: result.json });
    }

    private async loadFailureModes() {
        const url = `/api/surveys/${this.props.match.params.id}/failureModes`;
        const result = await fetchGet(url, {
            includeAssets: 'true',
            includeFindings: 'true',
            includeMaintenance: 'true',
        });
        await this.setState({ failureModes: result.json });
    }

    private async loadInspectionPlans() {
        const url = `/api/surveys/${this.props.match.params.id}/inspectionPlans`;
        const result = await fetchGet(url);
        await this.setState({ inspectionPlans: result.json.items });
    }

    private async loadStartPageAssets() {
        const url = `/api/assets?surveyId=${this.props.match.params.id}&onstartpage=true&types=${assetTypes.ImageAnnotation},${assetTypes.Image},${assetTypes.Image360}`;
        const result = await fetchGet(url);
        await this.setState({ startPageAssets: result.json });
    }

    private scrollTo(this: void, name: string) {
        scroller.scrollTo(name, {
            duration: 800,
            delay: 100,
            smooth: true,
        });
    }

    private onExpandAll() {
        const breakDownOpenState = {} as { [key: string]: boolean };
        for (const element of this.state.elements) {
            breakDownOpenState[element.id] = true;
            for (const unit of element.units) {
                breakDownOpenState[unit.id] = true;
                for (const manifestation of unit.manifestations) {
                    breakDownOpenState[manifestation.id] = true;
                }
            }
        }
        void this.setState({ breakDownOpenState });
    }

    private onExpandDecomposition() {
        const breakDownOpenState = {} as { [key: string]: boolean };
        for (const element of this.state.elements) {
            breakDownOpenState[element.id] = true;
            for (const unit of element.units) {
                if (unit.manifestations.length !== 0) {
                    breakDownOpenState[unit.id] = true;
                }
            }
        }
        void this.setState({ breakDownOpenState });
    }

    private onCollapseAll() {
        void this.setState({ breakDownOpenState: {} });
    }

    private editReport(e: any) {
        e.preventDefault();
        if (this.state.inspectionStandardType === InspectionStandardTypes.Fmeca) {
            this.props.navigate(`/surveys/${this.props.match.params.id}/analysis`);
        } else if (this.state.inspectionStandardType === InspectionStandardTypes.Memo) {
            this.props.navigate(`/surveys/${this.props.match.params.id}/summaryAndAdvice`);
        } else if (this.state.inspectionStandardType === InspectionStandardTypes.Model3d) {
            this.props.navigate(`/surveys/${this.props.match.params.id}/pointcloud`);
        } else if (
            [
                InspectionStandardTypes.Free,
                InspectionStandardTypes.Nen2767,
                InspectionStandardTypes.Api653,
                InspectionStandardTypes.Api570,
                InspectionStandardTypes.Eemua159,
            ].includes(this.state.inspectionStandardType!)
        ) {
            this.props.navigate(`/surveys/${this.props.match.params.id}/conditionAssessment`);
        } else {
            this.props.navigate(`/surveys/${this.props.match.params.id}/editReport`);
        }
    }

    private getInspectionStandardTypeSettings() {
        return customInspectionTypeFrontEndSettings[this.state.inspectionStandardType as string];
    }

    private getLeftTrayListItems(): (props: { survey: ISurvey }) => JSX.Element | null {
        if (!this.state.id) {
            return () => null;
        }
        const settings = this.getInspectionStandardTypeSettings();
        if (settings) {
            // eslint-disable-next-line @typescript-eslint/unbound-method
            return settings.getSurveyDetailsLeftTrayListItems;
        }

        return () => null;
    }

    private getScrollMenuItems(): (props: { scrollTo: (name: string) => void }) => JSX.Element | null {
        if (!this.state.id) {
            return () => null;
        }
        const settings = this.getInspectionStandardTypeSettings();
        if (settings) {
            // eslint-disable-next-line @typescript-eslint/unbound-method
            return settings.getSurveyDetailsScrollMenuItems;
        }
        return () => null;
    }

    private getDetailComponents(): (props: { survey: Required<ISurvey> }) => JSX.Element | null {
        if (!this.state.id) {
            return () => null;
        }
        const settings = this.getInspectionStandardTypeSettings();
        if (settings) {
            // eslint-disable-next-line @typescript-eslint/unbound-method
            return settings.getSurveyDetailsComponents;
        }
        return () => null;
    }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const SurveyDetails = translate()(connect(mapStateToProps, undefined, undefined, { forwardRef: true })(SurveyDetailsComponent as any) as any);
